import { Layout } from '@components/common'
import type {
    GetStaticPropsContext,
    GetStaticPropsResult,
    InferGetStaticPropsType
} from 'next'
import cn from 'classnames'
import s from '@pagestyles/Ooka.for.business.module.scss'
import cs from '@pagestyles/Cms.module.scss'

import slickStyles from '@commonStyles/Slick.module.scss'
import getCMSPage from '@pylot-data/api/operations/get-cms'
import { AlfaRichContent as RichContent } from '@components/alfakher/pylot-rich-content'
import React, { ReactElement, useEffect, useState } from 'react'
import type { CmsPage } from '@pylot-data/pylotschema'
import { serverSideTranslations } from 'next-i18next/serverSideTranslations'
import { useCmsPage } from '@pylot-data/hooks/cms/use-cms-page'
import { SEO, SEO_ENUMS } from '@components/seo'
import { ProductItem } from '@components/common/ProductTile/ProductTile'
import { iframeVideoPluginInit } from '@config/hooks/videoController'
// Highly unlikely to change. Even if admin user wats to change the content, they can swap page identifiers.
const CMS_PAGE_IDENTIFIER = 'ooka-for-business'

export async function getStaticProps({
    locale
}: GetStaticPropsContext): Promise<
    GetStaticPropsResult<{ cmsPageData: CmsPage | null | undefined }>
> {
    const cmsPageData = await getCMSPage({
        identifier: CMS_PAGE_IDENTIFIER,
        locale
    })

    return {
        props: {
            cmsPageData,
            ...(await serverSideTranslations(locale!, ['common']))
        },
        revalidate: 14400
    }
}

export default function Home({
    cmsPageData
}: InferGetStaticPropsType<typeof getStaticProps>): ReactElement {
    const { cmsPage = cmsPageData } = useCmsPage(CMS_PAGE_IDENTIFIER, {
        revalidateOnFocus: false,
        revalidateOnMount: true,
        initialData: cmsPageData
    })

    const [scrollInitiated, setScrollInitiated] = useState(false)

    if (typeof window != 'undefined') {
        iframeVideoPluginInit()
    }

    useEffect(() => {
        const handleScroll = () => {
            if (!scrollInitiated) {
                setScrollInitiated(true)
                document.removeEventListener('scroll', handleScroll)
            }
        }

        document.addEventListener('scroll', handleScroll)
    }, [])

    useEffect(() => {
        const handleLoad = () => {
            const checkIframe = setInterval(() => {
                const iframe = document.getElementById(
                    'iframe'
                ) as HTMLIFrameElement
                if (iframe) {
                    const currentLocation = window.location.href.split('#')[0]
                    const urlParams = new URL(currentLocation).searchParams

                    const utmMedium = urlParams.get('utm_medium')
                    const utmSource = urlParams.get('utm_source')

                    iframe.contentWindow?.postMessage(
                        { utm_medium: utmMedium, utm_source: utmSource },
                        '*'
                    )

                    clearInterval(checkIframe)
                } else {
                    console.error('IFrame is null.')
                }
            }, 500)
        }
        window.addEventListener('load', handleLoad)

        return () => {
            window.removeEventListener('load', handleLoad)
        }
    }, [])

    /* eslint-disable i18next/no-literal-string */
    return (
        <div className={cn(s['ooka-for-business'], slickStyles.root)}>
            <SEO page={SEO_ENUMS.HOME} cmsPage={cmsPage} />
            <RichContent html={cmsPage?.content} ProductTile={ProductItem} />
        </div>
    )
}

Home.Layout = Layout
